import type { RouteLocationNormalizedLoaded } from 'vue-router';
import type { Integration } from '@/types';

const HIDDEN_INTEGRATION_TYPES = new Set<string>([]);

const INTEGRATION_ROUTE_SEGMENTS: Record<string, string> = {
  cameras: 'cameras',
  knx: 'knx',
  bacnet: 'bacnet',
  modbus: 'modbus',
  mqtt: 'mqtt',
  matter: 'matter',
  matter_bridge: 'matter-bridge',
  'otto-touch': 'touch',
  computer: 'computers',
  zwave: 'zwave',
  zigbee: 'zigbee',
  homeassistant: 'homeassistant',
  esphome: 'esphome',
  wled: 'wled',
  unifi: 'unifi',
  hue: 'hue',
  somfy: 'somfy',
  synology: 'synology',
  torik: 'torik',
  media_player: 'media-players',
  spotify: 'spotify',
  sonos: 'sonos',
};

// Base-relative so integration icons resolve under Electron's file:// origin
// (built with base "./") as well as the web build (base "/"). Same pattern as
// the Torik app icon in ServerTopBar.vue.
const INTEGRATION_ICON_BASE = `${import.meta.env.BASE_URL}images/`;

const INTEGRATION_ICON_IMAGES: Record<string, string> = {
  knx: `${INTEGRATION_ICON_BASE}KNX.svg`,
  bacnet: `${INTEGRATION_ICON_BASE}BACnet.svg`,
  modbus: `${INTEGRATION_ICON_BASE}Modbus.svg`,
  mqtt: `${INTEGRATION_ICON_BASE}Mqtt.svg`,
  matter: `${INTEGRATION_ICON_BASE}Matter.svg`,
  matter_bridge: `${INTEGRATION_ICON_BASE}Matter.svg`,
  zigbee: `${INTEGRATION_ICON_BASE}ZigBee.svg`,
  zwave: `${INTEGRATION_ICON_BASE}Zwave.svg`,
  esphome: `${INTEGRATION_ICON_BASE}ESPHome.svg`,
  dali: `${INTEGRATION_ICON_BASE}Dali.svg`,
  homeassistant: `${INTEGRATION_ICON_BASE}HomeAssistant.svg`,
  wled: `${INTEGRATION_ICON_BASE}WLED.svg`,
  hue: `${INTEGRATION_ICON_BASE}Hue.svg`,
  torik: `${INTEGRATION_ICON_BASE}TorikHub.svg`,
};

export function normalizeHubPath(path: string): string {
  return path.replace(/^\/h\/[^/]+/, '');
}

/**
 * A hub path, scoped to the hub the current route belongs to.
 *
 * A scoped page (`/h/:hubId/…`) that pushes a NON-scoped hub route
 * (`/users/<id>`, `/devices`) silently leaves the hub scope: in an Electron
 * cloud tab the auth guard then has neither a local token (cloud sessions carry
 * none) nor a hub-scoped route, so it redirects to `/home` — the tab falls back
 * to the launcher ("Home Layout") and the page the user clicked never opens.
 * Mirror of `getSettingsPath` for the rest of the hub surfaces.
 */
export function getHubPath(path: string, route?: RouteLocationNormalizedLoaded): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const hubId = route?.params.hubId;

  return hubId ? `/h/${String(hubId)}${normalizedPath}` : normalizedPath;
}

export function getSettingsPath(path: string, route?: RouteLocationNormalizedLoaded): string {
  const normalizedPath = path.startsWith('/settings') ? path : `/settings${path.startsWith('/') ? path : `/${path}`}`;
  return getHubPath(normalizedPath, route);
}

export function getIntegrationRouteSegment(integrationType: string): string {
  return INTEGRATION_ROUTE_SEGMENTS[integrationType] ?? 'datapoints';
}

export function getIntegrationSettingsPath(
  integration: Pick<Integration, 'id' | 'integrationType'>,
  route?: RouteLocationNormalizedLoaded,
): string {
  return getSettingsPath(
    `/settings/integrations/${integration.id}/${getIntegrationRouteSegment(integration.integrationType)}`,
    route,
  );
}

export function isVisibleIntegration(integration: Pick<Integration, 'integrationType'>): boolean {
  return !HIDDEN_INTEGRATION_TYPES.has(integration.integrationType);
}

export function getVisibleIntegrations(integrations: Integration[]): Integration[] {
  return integrations
    .filter(isVisibleIntegration)
    .slice()
    .sort((a, b) => a.integrationName.localeCompare(b.integrationName));
}

export function isSettingsIntegrationsRoute(route: RouteLocationNormalizedLoaded): boolean {
  const path = normalizeHubPath(route.path);
  return path === '/settings/integrations' || path.startsWith('/settings/integrations/');
}

export function isIntegrationRouteActive(route: RouteLocationNormalizedLoaded, integrationId: string): boolean {
  const path = normalizeHubPath(route.path);
  return (
    path === `/settings/integrations/${integrationId}` || path.startsWith(`/settings/integrations/${integrationId}/`)
  );
}

export function getIntegrationIconImage(type: string): string | undefined {
  return INTEGRATION_ICON_IMAGES[type];
}

export function getIntegrationIcon(type: string): string {
  switch (type) {
    case 'knx':
      return '🔌';
    case 'bacnet':
      return '🏢';
    case 'modbus':
      return '🏭';
    case 'mqtt':
      return '📨';
    case 'matter':
      return '♾️';
    case 'matter_bridge':
      return '🌉';
    case 'otto-touch':
      return '🖥️';
    case 'computer':
      return '💻';
    case 'zigbee':
      return '📡';
    case 'zwave':
      return '🌊';
    case 'esphome':
      return '🔧';
    case 'dali':
      return '💡';
    case 'wled':
      return '💡';
    case 'unifi':
      return '📶';
    case 'hue':
      return '💡';
    case 'somfy':
      return '🪟';
    case 'synology':
      return '🗄️';
    case 'camera':
    case 'cameras':
      return '📹';
    case 'media_player':
      return '🎵';
    case 'spotify':
      return '🎧';
    case 'sonos':
      return '🔊';
    case 'homeassistant':
      return '🏠';
    case 'torik':
      return '🔗';
    default:
      return '🧩';
  }
}
